import React from 'react';
import {
  CheckCircleIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useTranslation, Trans } from 'react-i18next';
import SelectableCardGroup from '../../../../ui/molecules/SelectableCardGroup';
import SourcesSearch from '../../../../pages/Articles/GenerateDraft/Sources/SourcesSearch';
import CreateEditorToolbar from './CreateEditorToolbar';

/**
 * Step 2 (content): EditorJS context/draft editor, bulk settings and summary source selection.
 * Keeps the `#editorjs` holder mounted exactly like the page did.
 * Extracted verbatim from pages/Articles/CreateArticle.js.
 */
export default function ContentStep({
  articleContext,
  articleContextError,
  articleType,
  bulkTopic,
  bulkTopicError,
  contentType,
  editorElementRef,
  handleRemoveSelectedArticleForSummary,
  handleSelectedArticleForSummary,
  isDraftGenerated,
  knowledgeMode,
  language,
  numberOfArticles,
  selectedArticleForSummary,
  setBulkTopic,
  setBulkTopicError,
  setKnowledgeMode,
  setNumberOfArticles,
  triggerSourcesSearch,
  runEditorCommand,
  convertCurrentBlock,
  insertEditorBlock,
}) {
  const { t } = useTranslation();

  return (
    <section id="content" className="mb-16">
      {articleType === 'bulk' ? (
        /* Bulk Articles Settings */
        <>
          <div className="mb-8">
            <h2 className="text-xl font-medium mb-1">{t('articles.create.bulk.title')}</h2>
            <p className="text-base text-gray-500 mb-6">
              {t('articles.create.bulk.description')}
            </p>

            {/* Number of Articles */}
            <div className="mb-6">
              <label htmlFor="number-of-articles" className="block text-sm font-medium text-gray-700 mb-2">
                {t('articles.create.bulk.articlesCountLabel')}
              </label>
              <input
                type="number"
                id="number-of-articles"
                min="1"
                max="50"
                value={numberOfArticles}
                onChange={(e) => setNumberOfArticles(parseInt(e.target.value) || 1)}
                className="form-input max-w-xs py-2"
                placeholder="5"
              />
              <p className="mt-1 text-sm text-gray-500">{t('articles.create.bulk.articlesCountHint')}</p>
            </div>

            {/* Bulk Topic */}
            <div className="mb-6">
              <label htmlFor="bulk-topic" className="block text-sm font-medium text-gray-700 mb-2">
                {t('articles.create.bulk.topicLabel')}
              </label>
              <textarea
                id="bulk-topic"
                rows={4}
                className={`form-input ${bulkTopicError ? 'border-red-300' : ''}`}
                value={bulkTopic}
                onChange={(e) => {
                  setBulkTopic(e.target.value);
                  if (e.target.value.trim() !== '') {
                    setBulkTopicError('');
                  }
                }}
                placeholder={t('articles.create.bulk.topicPlaceholder')}
              />
              {bulkTopicError && (
                <p className="mt-1 text-sm text-red-600">{bulkTopicError}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                {t('articles.create.bulk.topicHint', { count: numberOfArticles })}
              </p>
            </div>
          </div>
        </>
      ) : (
        /* Single Article Context - EditorJS as primary input */
        <div className="">
          {contentType === 'changelog' && !isDraftGenerated && (
            <p className="mb-4 text-base text-gray-500">{t('articles.create.changelogContext.hint')}</p>
          )}
          <div className="mt-1">
            <div className={`create-article-editor bg-white rounded  ${articleContextError ? 'border-red-300' : 'border-gray-200'}`}>
              {isDraftGenerated && (
                <CreateEditorToolbar
                  runEditorCommand={runEditorCommand}
                  convertCurrentBlock={convertCurrentBlock}
                  insertEditorBlock={insertEditorBlock}
                />
              )}
              <div
                id="editorjs"
                ref={editorElementRef}
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  minHeight: '320px'
                }}
              />
            </div>
            {articleContextError && (
              <p className="mt-1 text-sm text-red-600">{articleContextError}</p>
            )}
          </div>
        </div>
      )}


      {/* Sources Section - FIRST for summary content type */}
      {articleType === 'single' && contentType === 'summary' && (
      <div className="mb-8 border-b pb-8 border-gray-200">
        {/* Unified Sources header for all content types */}
        <div className="mb-6">
          <h2 className="text-xl font-medium mb-1">{t('articles.create.summary.externalSourcesTitle')}</h2>
          <p className="text-base text-gray-500 mb-4">
            <Trans
              i18nKey="articles.create.summary.externalSourcesDescription"
              components={{ strong: <strong /> }}
            />
          </p>
        </div>

        {/* Knowledge Mode Toggle - only for summary content type */}
        {contentType === 'summary' && (
          <div className="mb-6">
            <SelectableCardGroup
              options={[
                {
                  id: 'manual',
                  title: t('articles.create.summary.manualSelection'),
                  icon: MagnifyingGlassIcon,
                  description: t('articles.create.summary.manualSelectionDescription')
                },
                {
                  id: 'automatic',
                  title: t('articles.create.summary.automaticSelection'),
                  icon: SparklesIcon,
                  description: t('articles.create.summary.automaticSelectionDescription')
                }
              ]}
              selectedId={knowledgeMode}
              onSelect={setKnowledgeMode}
              columns={2}
            />
          </div>
        )}

        <div className="space-y-4">

          {/* Automatic mode info for summary */}
          {contentType === 'summary' && knowledgeMode === 'automatic' && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 text-blue-500 mt-0.5">
                  🤖
                </div>
                <div>
                  <h4 className="font-medium text-blue-900 text-sm">{t('articles.create.summary.automaticKnowledgeTitle')}</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    <Trans
                      i18nKey="articles.create.summary.automaticKnowledgeDescription"
                      values={{ topic: articleContext }}
                      components={{ strong: <strong /> }}
                    />
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    {t('articles.create.summary.automaticKnowledgeHint')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Manual mode - show search interface */}
          {contentType === 'summary' && knowledgeMode === 'manual' && (
            <>
              {/* Search button - hidden, SourcesSearch component handles search directly */}

              {/* Selected article display */}
              {selectedArticleForSummary && (
                <div className="mb-4">
                  <div className="p-3 border border-primary-500 rounded-lg bg-primary-50">
                    <div className="flex items-start gap-3">
                      <CheckCircleIcon className="w-5 h-5 text-primary-500 mt-1 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h5 className="font-medium text-gray-900 text-sm line-clamp-2">
                          {selectedArticleForSummary.title}
                        </h5>
                        <p className="text-xs text-gray-600 mt-1">
                          {selectedArticleForSummary.authors} • {selectedArticleForSummary.journal} • {selectedArticleForSummary.pubDate}
                        </p>
                        {selectedArticleForSummary.doi && (
                          <p className="text-xs text-gray-500 mt-1">
                            DOI: {selectedArticleForSummary.doi}
                          </p>
                        )}
                        <p className="text-xs text-primary-600 mt-1">
                          {selectedArticleForSummary.source} - {selectedArticleForSummary.id}
                        </p>
                      </div>
                      <button
                        onClick={handleRemoveSelectedArticleForSummary}
                        className="ml-2 text-red-500 hover:text-red-700 flex-shrink-0"
                        title={t('articles.create.summary.removeSelectedArticle')}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Sources Search component - always visible in manual mode */}
              <SourcesSearch
                articleContext={articleContext}
                sources={selectedArticleForSummary ? [selectedArticleForSummary] : []}
                onSourceSelect={handleSelectedArticleForSummary}
                triggerSearch={triggerSourcesSearch}
                language={language}
                key="summary-article-search-component"
                onSearchComplete={() => { }}
                onClose={() => {}}
              />
            </>
          )}
        </div>
      </div>
      )}

    </section>
  );
}
